import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography
} from '@mui/material'
import React, { useState } from 'react'

import { AccordionSection } from '@f-fertighaus/applications/Next/components/fertighaus/AccordionSection/AccordionSection'
import { AngleIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/AngleIcon'
import { HomeSizeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HomeSizeIcon'
import { RoofAnglesIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RoofAnglesIcon'
import { RoofTypeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RoofTypeIcon'
import { InfoTooltip } from '@f-fertighaus/applications/Next/components/fertighaus/InfoTooltip/InfoTooltip'
import {
  DictionaryKey,
  t
} from '@f-fertighaus/applications/Next/public/locales/de'
import { ROOF_BY_NUMBER } from '@f-fertighaus/applications/Next/utils/backendStructureMappings'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { millimetersToCentimeters } from '@f-fertighaus/applications/Next/utils/millimetersToCentimeters'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { DetailValueWithTooltip } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailElement/DetailElement.styled'
import { DetailList } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailList.styled'
import { DetailsSectionWrapper } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailsSectionWrapper.styled'
import { RoofVisualizationBox } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/RoofInformation/RoofInformation.styled'
import { ClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'

import { DetailElement } from '../common/DetailElement/DetailElement'

const ROOF_KEY_BASE = 'houseDetailPage.sections.details.roof'

const ROOF_TYPES_WITH_DESCRIPTION = [
  'saddle_roof',
  'tent_roof',
  'hipped_roof',
  'half_hipped_roof',
  'flat_roof',
  'pent_roof'
] as const

export const RoofInformation = () => {
  const {
    state: { roof_shape_name, roof_shape, roof_pitch, knee_wall }
  } = useHouseDetail()
  const [accordionExpanded, setAccordionExpanded] = useState(false)

  const {
    type: roofShapeType,
    imageClassName: roofImageClassName
  } = ROOF_BY_NUMBER[roof_shape || 1]
  const shouldDisplayRoofVisualization =
    roofImageClassName || roof_pitch || knee_wall
  const kneeWallText = knee_wall
    ? t(`${ROOF_KEY_BASE}.values.knee_wall` as DictionaryKey, {
        number: millimetersToCentimeters(knee_wall)
      })
    : undefined

  const handleAccordionClick = () => {
    webtrack({
      category: 'accordion',
      action: 'click',
      label: `${accordionExpanded ? 'collapse' : 'expand'}-roof-options`,
      location: 'details-section',
      values: [
        {
          type: 'text',
          value: t('houseDetailPage.sections.details.roof.accordion.headline')
        }
      ]
    })
    setAccordionExpanded(!accordionExpanded)
  }

  if (!roof_shape_name || !roof_shape) {
    return null
  }

  return (
    <DetailsSectionWrapper>
      <Box sx={{ pt: 1, pb: 2 }}>
        <Typography variant='bodyTitle' component='p'>
          {t(`${ROOF_KEY_BASE}.headline` as DictionaryKey)}
        </Typography>
      </Box>
      <DetailList>
        <DetailElement
          icon={RoofTypeIcon}
          name={t(`${ROOF_KEY_BASE}.labels.roof_shape_name` as DictionaryKey)}>
          <DetailValueWithTooltip>
            <Typography variant='body2'>{roof_shape_name}</Typography>
            <ClientOnly>
              <InfoTooltip
                title={t(
                  `${ROOF_KEY_BASE}.tooltips.${roofShapeType}` as DictionaryKey
                )}
                wtPayload={{
                  location: 'details-section',
                  label: roofShapeType
                }}
              />
            </ClientOnly>
          </DetailValueWithTooltip>
        </DetailElement>
        {typeof roof_pitch === 'number' && roof_pitch > 0 && (
          <DetailElement
            icon={AngleIcon}
            name={t(`${ROOF_KEY_BASE}.labels.roof_pitch` as DictionaryKey)}>
            <Typography variant='body2'>{`${roof_pitch}°`}</Typography>
          </DetailElement>
        )}
        {kneeWallText && (
          <DetailElement
            icon={HomeSizeIcon}
            name={t(`${ROOF_KEY_BASE}.labels.knee_wall` as DictionaryKey)}>
            <DetailValueWithTooltip>
              <Typography variant='body2'>{kneeWallText}</Typography>
              <ClientOnly>
                <InfoTooltip
                  title={t(
                    `${ROOF_KEY_BASE}.tooltips.knee_wall` as DictionaryKey
                  )}
                  wtPayload={{
                    location: 'details-section',
                    label: 'knee_wall'
                  }}
                />
              </ClientOnly>
            </DetailValueWithTooltip>
          </DetailElement>
        )}
      </DetailList>
      {shouldDisplayRoofVisualization && (
        <RoofVisualizationBox container alignItems={'center'}>
          {roofImageClassName && (
            <Grid item xs={6}>
              <div className={`roof-shape ${roofImageClassName}`} />
            </Grid>
          )}

          {(roof_pitch || knee_wall) && (
            <RoofAnglesIcon angle={roof_pitch} knee_wall={kneeWallText} />
          )}
        </RoofVisualizationBox>
      )}
      <Accordion>
        <AccordionSummary onClick={handleAccordionClick}>
          <Typography variant='body1'>
            {t(`${ROOF_KEY_BASE}.accordion.headline` as DictionaryKey)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {ROOF_TYPES_WITH_DESCRIPTION.map(value => (
            <AccordionSection
              key={value}
              headline={t(
                `${ROOF_KEY_BASE}.accordion.${value}.headline` as DictionaryKey
              )}
              description={t(
                `${ROOF_KEY_BASE}.accordion.${value}.description` as DictionaryKey
              )}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    </DetailsSectionWrapper>
  )
}
